/**
 * A titled list section for the Ryerson Commitment pages. Each list item is either a PDF link
 * (when it has a `url`) or plain text with a dot icon. From `lg` up the title and description sit
 * above a dark panel that lays the items out in three columns; below `lg` the title is left to the
 * page's `AllSections` accordion tab and the items stack.
 *
 * Used as a section by four pages under `src/pages/why-ryerson/ryerson-commitment/`:
 * `ryerson-gives-back/index.tsx` (By the Numbers), `why-metal-matters/index.tsx` (The Essential
 * Metal), `product-quality/index.tsx` and `environment-health-safety/index.tsx`. Only the
 * Environment, Health & Safety list sets `url`:
 * `src/content/ryerson/why-ryerson/ryerson-commitment/environment-health-safety.ts` →
 * `SafetyDataSheetsContent`. The others, including the same-named list in `product-quality.ts`, are
 * text only.
 *
 * Every `url` resolves under one fixed folder of the static-assets host:
 * `NEXT_PUBLIC_URI_STATIC` + `/static-assets/documents/pdfs/<language>/safety-data-sheets/<url>`,
 * where `<language>` is the visitor's (`en`, `fr` or `es`). A PDF for any other list has to live in
 * that folder, in each language, or the link breaks.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import styled from "@emotion/styled";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { css } from "@emotion/react";
import { Link } from "@ryerson/frontend.navigation";
import { Icon } from "@ryerson/frontend.assets";
import { useApplication } from "@ryerson/frontend.application";
import rgba from "polished/lib/color/rgba";

/** One list item. */
export type FileListItem = {
	/** The visible text, and the link text when `url` is set. */
	name: string;
	/**
	 * PDF file name under the safety-data-sheets folder (see the file header). Absent: the item is
	 * plain text, not a link.
	 */
	url?: string;
};

/** One language's copy for the section. */
export type FileExplorerContent = {
	/** Drawn from `lg` up only; pages also pass it to `AllSections` as the section title. */
	title: string;
	description: string;
	fileList: FileListItem[];
};

/** Props of {@link FileExplorer}. */
export type FileExplorerProps = {
	content: FileExplorerContent;
};

const Title = styled(Typography)`
	margin-top: 0px;
	margin-bottom: 20px;
`;

const Description = styled(Typography)`
	margin-top: 20px;
	width: 380px;
`;

const ContentHeader = styled(Flex)`
	width: 100%;
`;

const SafetyDataSheetsContainer = styled.div`
	padding: 40px 60px 60px;
	margin-top: 30px;
	${(props: any) => {
		const { color } = props;
		return css`
			background-color: ${color};
		`;
	}}
`;

const PdfList = styled.ul`
	-moz-column-count: 3;
	-moz-column-gap: 30px;
	-webkit-column-count: 3;
	-webkit-column-gap: 30px;
	column-count: 3;
	column-gap: 30px;
	padding: 0;
`;

const PdfItem = styled.li`
	display: inline-block;
	width: 100%;
	padding-top: 28px;
`;

const PdfIcon = styled(Icon)`
	flex-shrink: 0;
`;

const PdfLink = styled(Link)`
	margin-left: 10px;
`;

const Underline = styled(Flex)`
	padding-bottom: 5px;
	width: fit-content;
`;

const MobilePdfList = styled.ul`
	padding: 0;
`;

const MobilePdfIcon = styled(Icon)`
	flex-shrink: 0;
`;

const MobilePdfItem = styled.li`
	display: flex;
	padding-top: 28px;
`;

const MobilePdfLink = styled(Link)`
	margin: 0px 10px;
`;
const MobileSafetyDataSheetsContainer = styled.div`
	margin-top: 30px;
	margin-bottom: 20px;
`;

/** Renders the list section for both breakpoints; PDF links open in a new tab. */
const FileExplorer: React.FC<FileExplorerProps> = ({ content }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();
	return (
		<>
			<Media lessThan="lg">
				<MobileSafetyDataSheetsContainer>
					<Description
						variant="p"
						size="md"
						css={css`
							width: 100%;
						`}
					>
						{content.description}
					</Description>
					<MobilePdfList>
						{content.fileList.map((safetyDataSheet: FileListItem, index: number) => {
							return safetyDataSheet.url ? (
								<>
									<MobilePdfItem key={index}>
										<MobilePdfIcon
											color={theme.colors.primary.link}
											icon="file-pdf"
											size="sm"
										/>
										<MobilePdfLink
											next={false}
											target="_blank"
											to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/safety-data-sheets/${safetyDataSheet.url}`}
										>
											{safetyDataSheet.name}
										</MobilePdfLink>
									</MobilePdfItem>
								</>
							) : (
								<>
									<MobilePdfItem key={index}>
										<MobilePdfIcon
											color={theme.colors.primary.link}
											icon="dot-circle"
											size="sm"
										/>
										<Typography
											color={theme.colors.primary.link}
											variant="p"
											size="lg"
											css={css`
												margin: 0px;
											`}
										>
											{safetyDataSheet.name}
										</Typography>
									</MobilePdfItem>
								</>
							);
						})}
					</MobilePdfList>
				</MobileSafetyDataSheetsContainer>
			</Media>
			<Media greaterThanOrEqual="lg">
				<ContentSection
					vPadding="30px"
					type="split"
					split={{
						splitDirection: "vertical",
						splitColors: [
							{
								color: theme.colors.tertiary.background,
								percentage: "70%",
							},
							{
								color: theme.colors.secondary.background,
								percentage: "30%",
							},
						],
					}}
				>
					<ContentHeader justifyContent="space-between">
						<FlexItem>
							<Title variant="h2" type="tertiary">
								{content.title}
							</Title>
						</FlexItem>
						<FlexItem css={{ paddingRight: "70px" }}>
							<Description variant="p" type="tertiary">
								{content.description}
							</Description>
						</FlexItem>
					</ContentHeader>
					<SafetyDataSheetsContainer color={theme.colors.primary.darkerGray}>
						<PdfList>
							{content.fileList.map(
								(safetyDataSheet: FileListItem, index: number) => {
									return safetyDataSheet.url ? (
										<>
											<PdfItem key={index}>
												<Underline
													css={css`
														border-bottom: 1px solid
															${rgba(
																theme.colors.primary.lighterGray,
																0.1
															)};
													`}
												>
													<PdfIcon
														color={theme.colors.tertiary.link}
														icon="file-pdf"
														size="sm"
													/>
													<PdfLink
														next={false}
														color={theme.colors.tertiary.link}
														target="_blank"
														to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/safety-data-sheets/${safetyDataSheet.url}`}
													>
														{safetyDataSheet.name}
													</PdfLink>
												</Underline>
											</PdfItem>
										</>
									) : (
										<>
											<PdfItem key={index}>
												<Underline
													css={css`
														border-bottom: 1px solid
															${theme.colors.primary.darkerGray};
													`}
												>
													<PdfIcon
														color={theme.colors.tertiary.link}
														icon="dot-circle"
														size="sm"
													/>
													<Typography
														color={theme.colors.tertiary.link}
														variant="p"
														size="md"
														css={css`
															margin: 0px;
														`}
													>
														{safetyDataSheet.name}
													</Typography>
												</Underline>
											</PdfItem>
										</>
									);
								}
							)}
						</PdfList>
					</SafetyDataSheetsContainer>
				</ContentSection>
			</Media>
		</>
	);
};

export default FileExplorer;
